/**
 * Sea state — the wave half of the weather picture, shown alongside the wind.
 * Wind tells us what's pushing on us; the SEA tells us how it'll feel underfoot,
 * and the two don't always line up. A Force 4 over the top of a big leftover
 * swell from yesterday's gale is a very different ride from a Force 4 on flat
 * water, and only the sea state shows that.
 *
 * We model two parts, the way a marine forecast does:
 *   - the WIND SEA, the short stuff the local wind is raising right now, which
 *     tracks the wind closely;
 *   - the SWELL, the long, smooth waves that have travelled in from a distant
 *     blow, with their own height, direction, and period that lag the local wind.
 * The combined "significant wave height" adds their ENERGIES (root-sum-square),
 * not their heights, because that's how real seas superpose.
 *
 * Two sources, mirroring the wind model:
 *   - a SYNTHETIC training field, deterministic and tied to the synthetic wind so
 *     a scenario's wind and sea agree (wind sea from the local wind, plus a slow
 *     swell that wanders on its own);
 *   - a REAL field from Open-Meteo's marine API (wave height, swell height,
 *     swell direction, swell period) for real-area charts, fetched as JSON like
 *     the wind. Synthetic data is for training; not a real forecast.
 */
import { windAt, type WeatherSourceId } from './index';
import { TIDAL_DIAMONDS } from '@/tides';

/** A sea-state reading at one point. */
export interface SeaStateSample {
  /** [lon, lat]. */
  position: [number, number];
  /** Significant combined wave height (wind sea + swell), metres. */
  waveHeightM: number;
  /** Direction the sea comes FROM, degrees true, or null if not forecast. */
  waveFromDeg: number | null;
  /** Mean wave period, seconds, or null if not forecast. */
  wavePeriodS: number | null;
  /** Primary swell height, metres. */
  swellHeightM: number;
  /** Direction the swell comes FROM, degrees true. */
  swellFromDeg: number;
  /** Mean swell period, seconds. */
  swellPeriodS: number;
}

/**
 * Douglas sea scale (WMO degree of sea): the standard 0..9 description of wave
 * height, so the overlay can say "moderate" not just "1.4 m". Bands are the
 * upper bound of the significant height for each code.
 */
export interface SeaStateBand {
  code: number;
  label: string;
  /** Upper bound of significant height for this code, metres (Infinity for 9). */
  maxM: number;
}

export const DOUGLAS_SEA_SCALE: SeaStateBand[] = [
  { code: 0, label: 'Calm (glassy)', maxM: 0 },
  { code: 1, label: 'Calm (rippled)', maxM: 0.1 },
  { code: 2, label: 'Smooth', maxM: 0.5 },
  { code: 3, label: 'Slight', maxM: 1.25 },
  { code: 4, label: 'Moderate', maxM: 2.5 },
  { code: 5, label: 'Rough', maxM: 4 },
  { code: 6, label: 'Very rough', maxM: 6 },
  { code: 7, label: 'High', maxM: 9 },
  { code: 8, label: 'Very high', maxM: 14 },
  { code: 9, label: 'Phenomenal', maxM: Infinity },
];

/** The Douglas sea-state band for a significant wave height. */
export function douglasSeaState(waveHeightM: number): SeaStateBand {
  const h = Math.max(0, waveHeightM);
  for (const b of DOUGLAS_SEA_SCALE) {
    if (h <= b.maxM) return b;
  }
  return DOUGLAS_SEA_SCALE[DOUGLAS_SEA_SCALE.length - 1];
}

/**
 * The wind sea (short, locally-raised waves) significant height from the wind
 * speed. Same open-water Beaufort relation the comfort read uses: ~1 m at F4,
 * ~2 m at F5. Sheltered water carries less, so this over-reads inshore.
 */
export function windSeaHeight(windKn: number): number {
  const w = Math.max(0, windKn);
  return Math.round(Math.pow(w / 13.5, 2) * 100) / 100;
}

const DAY_MS = 86_400_000;

function hash01(n: number): number {
  const s = Math.sin(n * 91.7 + 47.3) * 27182.845;
  return s - Math.floor(s);
}
function smooth(x: number): number {
  const i = Math.floor(x);
  const f = x - i;
  const a = hash01(i);
  const b = hash01(i + 1);
  const t = f * f * (3 - 2 * f);
  return a + (b - a) * t;
}
function deg(n: number): number {
  return ((n % 360) + 360) % 360;
}

/**
 * Synthetic SWELL at an instant and place: a long-period train that wanders
 * slowly on its own clock, lagging the wind so it reads like the leftover of an
 * earlier blow from somewhere upwind. Deterministic: same inputs, same swell.
 */
function syntheticSwell(
  ms: number,
  lon: number,
  lat: number,
): {
  heightM: number;
  fromDeg: number;
  periodS: number;
} {
  const days = ms / DAY_MS;
  // Swell builds and decays over several days, slower than the wind sea.
  const heightM = Math.max(0, 0.4 + smooth(days * 0.35 + 9) * 2.2);
  // Direction wanders gently around the prevailing SW swell window.
  const fromDeg = deg(
    230 + (smooth(days * 0.3 + 2) - 0.5) * 2 * 60 + (smooth(lon * 3.1 + lat * 2.7) - 0.5) * 20,
  );
  // Longer swell carries more energy: 7..13 s, rising with height.
  const periodS = Math.round((7 + Math.min(1, heightM / 2.6) * 6) * 10) / 10;
  return { heightM: Math.round(heightM * 100) / 100, fromDeg, periodS };
}

/** Combine wind-sea and swell heights by energy (root-sum-square). */
export function combinedWaveHeight(windSeaM: number, swellM: number): number {
  return Math.round(Math.hypot(Math.max(0, windSeaM), Math.max(0, swellM)) * 100) / 100;
}

/**
 * The SYNTHETIC sea state at a point/instant, tied to the synthetic wind so the
 * wind sea and the wind agree. `source` is carried for call-site symmetry with
 * the wind model; the synthetic field is the only one returned here (the real
 * field comes over the network, like the real wind).
 */
export function seaStateAt(
  source: WeatherSourceId,
  ms: number,
  position: [number, number],
): SeaStateSample {
  const wind = windAt(source, ms, position);
  const windSea = windSeaHeight(wind.speedKn);
  const swell = syntheticSwell(ms, position[0], position[1]);
  return {
    position,
    waveHeightM: combinedWaveHeight(windSea, swell.heightM),
    // The synthetic model has no separate wind-sea direction or period, and
    // inventing one would be worse than saying so. The swell carries both.
    waveFromDeg: null,
    wavePeriodS: null,
    swellHeightM: swell.heightM,
    swellFromDeg: swell.fromDeg,
    swellPeriodS: swell.periodS,
  };
}

/** Bounding box of the training area, from the charted tidal diamonds. */
function trainingBounds(): { minLon: number; minLat: number; maxLon: number; maxLat: number } {
  let minLon = Infinity,
    minLat = Infinity,
    maxLon = -Infinity,
    maxLat = -Infinity;
  for (const d of TIDAL_DIAMONDS) {
    const [lon, lat] = d.position;
    if (lon < minLon) minLon = lon;
    if (lat < minLat) minLat = lat;
    if (lon > maxLon) maxLon = lon;
    if (lat > maxLat) maxLat = lat;
  }
  if (!Number.isFinite(minLon)) {
    return { minLon: -6.4, minLat: 45.7, maxLon: -5.4, maxLat: 46.4 };
  }
  const padLon = (maxLon - minLon) * 0.15 + 0.05;
  const padLat = (maxLat - minLat) * 0.15 + 0.05;
  return {
    minLon: minLon - padLon,
    minLat: minLat - padLat,
    maxLon: maxLon + padLon,
    maxLat: maxLat + padLat,
  };
}

/** A grid of synthetic sea-state samples across the training area, same instant → same field. */
export function seaStateFieldAt(
  source: WeatherSourceId,
  ms: number,
  cols = 9,
  rows = 7,
): SeaStateSample[] {
  const b = trainingBounds();
  const out: SeaStateSample[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const lon = b.minLon + ((b.maxLon - b.minLon) * c) / (cols - 1);
      const lat = b.minLat + ((b.maxLat - b.minLat) * r) / (rows - 1);
      out.push(seaStateAt(source, ms, [lon, lat]));
    }
  }
  return out;
}

// -----------------------------------------------------------------------------
// Real marine field (Open-Meteo marine API), mirroring the real wind field.
// -----------------------------------------------------------------------------

export const OPEN_METEO_MARINE_ATTRIBUTION = 'Marine data by Open-Meteo.com (CC BY 4.0)';

/** Build the Open-Meteo marine request: hourly wave + swell for each point. */
export function buildMarineUrl(points: [number, number][]): string {
  const lat = points.map((p) => p[1].toFixed(4)).join(',');
  const lon = points.map((p) => p[0].toFixed(4)).join(',');
  return (
    'https://marine-api.open-meteo.com/v1/marine' +
    `?latitude=${lat}&longitude=${lon}` +
    // The total sea as well as the swell. A skipper wants to know what the water
    // is actually doing, and "1.4 m from the south-west every 6 seconds" is a
    // different sea to "1.4 m from the south-west every 12 seconds".
    '&hourly=wave_height,wave_direction,wave_period,' +
    'swell_wave_height,swell_wave_direction,swell_wave_period' +
    '&timezone=UTC&forecast_days=3'
  );
}

/** The whole fetched marine field: per point, hourly series on a shared time grid. */
export interface RealSeaField {
  points: [number, number][];
  timesMs: number[];
  /** [pointIndex][hourIndex] significant wave height, metres. */
  waveHeightM: number[][];
  /** [pointIndex][hourIndex] mean wave direction FROM, degrees. */
  waveFromDeg: number[][];
  /** [pointIndex][hourIndex] mean wave period, seconds. */
  wavePeriodS: number[][];
  /** [pointIndex][hourIndex] swell height, metres. */
  swellHeightM: number[][];
  /** [pointIndex][hourIndex] swell direction FROM, degrees. */
  swellFromDeg: number[][];
  /** [pointIndex][hourIndex] swell period, seconds. */
  swellPeriodS: number[][];
}

interface MarineLoc {
  hourly?: {
    time?: string[];
    wave_height?: number[];
    wave_direction?: number[];
    wave_period?: number[];
    swell_wave_height?: number[];
    swell_wave_direction?: number[];
    swell_wave_period?: number[];
  };
}

export function parseSeaField(json: unknown, points: [number, number][]): RealSeaField {
  const arr = (Array.isArray(json) ? json : [json]) as MarineLoc[];
  let timesMs: number[] = [];
  const waveHeightM: number[][] = [];
  const waveFromDeg: number[][] = [];
  const wavePeriodS: number[][] = [];
  const swellHeightM: number[][] = [];
  const swellFromDeg: number[][] = [];
  const swellPeriodS: number[][] = [];
  // Number(null) is 0, which would turn "we have no forecast for this water" into
  // a flat calm. NaN instead, and the readers below drop it. See seaStateAtTime.
  const nums = (a: unknown): number[] => (Array.isArray(a) ? a.map((v) => (v == null ? NaN : Number(v))) : []);
  for (let i = 0; i < points.length; i++) {
    const h = arr[i]?.hourly;
    if (h?.time && !timesMs.length) timesMs = h.time.map((t) => Date.parse(`${t}Z`));
    waveHeightM.push(nums(h?.wave_height));
    waveFromDeg.push(nums(h?.wave_direction));
    wavePeriodS.push(nums(h?.wave_period));
    swellHeightM.push(nums(h?.swell_wave_height));
    swellFromDeg.push(nums(h?.swell_wave_direction));
    swellPeriodS.push(nums(h?.swell_wave_period));
  }
  return {
    points,
    timesMs,
    waveHeightM,
    waveFromDeg,
    wavePeriodS,
    swellHeightM,
    swellFromDeg,
    swellPeriodS,
  };
}

function nearestHourIndex(timesMs: number[], ms: number): number {
  let best = -1;
  let bestD = Infinity;
  for (let i = 0; i < timesMs.length; i++) {
    const d = Math.abs(timesMs[i] - ms);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

function nearestPointIndex(points: [number, number][], pos: [number, number]): number {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < points.length; i++) {
    const dx = points[i][0] - pos[0];
    const dy = points[i][1] - pos[1];
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/** Sea state at a position and instant: nearest grid point, nearest forecast hour. */
/**
 * Sea state at a position and instant: nearest grid point, nearest forecast hour.
 * NULL when there is no marine forecast for that water.
 *
 * That null is the important part. The marine model covers open sea and returns
 * nothing for a point up a loch, in a marina, or inland, and this used to read
 * those as zero and hand back a wave height of 0.0 m, which douglasSeaState then
 * described as "Calm (glassy)". A confident flat calm, invented, on a screen a
 * skipper might use to decide whether to go. No forecast and a flat calm are not
 * the same answer and must never look like it, so callers get null and have to
 * say so.
 */
export function seaStateAtTime(
  field: RealSeaField,
  pos: [number, number],
  ms: number,
): SeaStateSample | null {
  if (!field.points.length || !field.timesMs.length) return null;
  const p = nearestPointIndex(field.points, pos);
  const t = nearestHourIndex(field.timesMs, ms);
  if (t < 0) return null;
  /** A number the model actually gave us, or null. */
  const num = (a: number[][] | undefined): number | null => {
    const v = Number(a?.[p]?.[t]);
    return Number.isFinite(v) ? v : null;
  };
  // Wave height is the one that decides whether there is a forecast here at all;
  // period and direction can be absent in a sea flat enough to have no direction.
  const waveHeightM = num(field.waveHeightM);
  if (waveHeightM == null) return null;
  const swellHeightM = num(field.swellHeightM);
  return {
    position: pos,
    waveHeightM,
    waveFromDeg: num(field.waveFromDeg),
    wavePeriodS: num(field.wavePeriodS),
    swellHeightM: swellHeightM ?? 0,
    swellFromDeg: num(field.swellFromDeg) ?? 0,
    swellPeriodS: num(field.swellPeriodS) ?? 0,
  };
}

/** The sea-state snapshot at one instant (every grid point at that hour). */
export function seaFieldSamplesAt(field: RealSeaField, ms: number): SeaStateSample[] {
  const t = nearestHourIndex(field.timesMs, ms);
  if (t < 0) return [];
  const out: SeaStateSample[] = [];
  for (let i = 0; i < field.points.length; i++) {
    const num = (a: number[][]): number | null => {
      const v = Number(a[i]?.[t]);
      return Number.isFinite(v) ? v : null;
    };
    // A point the model does not cover is left out entirely. An overlay that
    // drew it would be painting a flat calm over water nobody has forecast.
    const waveHeightM = num(field.waveHeightM);
    if (waveHeightM == null) continue;
    out.push({
      position: field.points[i],
      waveHeightM,
      waveFromDeg: num(field.waveFromDeg),
      wavePeriodS: num(field.wavePeriodS),
      swellHeightM: num(field.swellHeightM) ?? 0,
      swellFromDeg: num(field.swellFromDeg) ?? 0,
      swellPeriodS: num(field.swellPeriodS) ?? 0,
    });
  }
  return out;
}

type FetchLike = (
  url: string,
) => Promise<{ ok: boolean; status: number; json: () => Promise<unknown> }>;

/** A grid of [lon, lat] points across [w, s, e, n], for the marine field. */
export function marineGrid(
  bounds: [number, number, number, number],
  cols = 8,
  rows = 6,
): [number, number][] {
  const [w, s, e, n] = bounds;
  const out: [number, number][] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const lon = w + ((e - w) * c) / (cols - 1);
      const lat = s + ((n - s) * r) / (rows - 1);
      out.push([lon, lat]);
    }
  }
  return out;
}

/** Fetch the whole hourly marine field over `bounds`. Throws on failure. */
export async function fetchRealSeaField(
  bounds: [number, number, number, number],
  fetchImpl: FetchLike = fetch as unknown as FetchLike,
): Promise<RealSeaField> {
  const points = marineGrid(bounds);
  const url = buildMarineUrl(points);
  const r = await fetchImpl(url);
  if (!r.ok) throw new Error(`Marine fetch failed (${r.status})`);
  return parseSeaField(await r.json(), points);
}
